"use client"

import { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  CircularProgress,
  IconButton,
  Alert,
  Snackbar,
  Grid,
  Divider,
  Select,
  MenuItem,
} from "@mui/material"
import { Close as CloseIcon } from "@mui/icons-material"
import DashboardLayout from "../components/DashboardLayout"
import { getAdminStaff, createAdminStaff, updateAdminStaff, resetAdminStaffState } from "../slices/adminStaffSlice"

const permissionsList = [
  { id: "adminStaff", name: "Admin Staff" },
  { id: "users", name: "Users" },
  { id: "campaign", name: "Campaign" },
  { id: "campaignConfig", name: "Campaign Config" },
  { id: "city", name: "City" },
  { id: "state", name: "State" },
  { id: "creditDetails", name: "Credit Details" },
  { id: "pendingCampaign", name: "Pending Campaign" },
  { id: "finishedCampaign", name: "Finished Campaign" },
  { id: "creditTransactions", name: "Credit Transactions" },
  { id: "whiteListing", name: "White Listing" },
  { id: "nonWhatsappNo", name: "Non-Whatsapp No." },
  { id: "scheduledCampaign", name: "Scheduled Campaign" },
  { id: "whiteListingDB", name: "White Listing DB" },
  { id: "campaignButton", name: "Campaign Button" },
  { id: "campaignNumbers", name: "Campaign Numbers" },
  { id: "countries", name: "Countries" },
  { id: "intCreditDetails", name: "Int Credit Details" },
  { id: "buttonCreditDetails", name: "Button Credit Details" },
  { id: "intCreditTransactions", name: "Int Credit Transactions" },
  { id: "buttonCreditTransactions", name: "Button Credit Transactions" },
  { id: "apiCampaign", name: "API Campaign" },
  { id: "manageCredits", name: "Manage Credits" },
]

const AdminStaffPage = () => {
  const dispatch = useDispatch()
  const { adminStaff, loading, error, success } = useSelector((state) => state.adminStaff)

  const [openDialog, setOpenDialog] = useState(false)
  const [dialogMode, setDialogMode] = useState("add") // "add" or "edit"
  const [currentStaffId, setCurrentStaffId] = useState(null)
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    status: "active",
    permissions: {},
  })
  const [alert, setAlert] = useState({ open: false, message: "", severity: "success" })

  useEffect(() => {
    dispatch(getAdminStaff())
  }, [dispatch])

  useEffect(() => {
    if (success) {
      setAlert({
        open: true,
        message: dialogMode === "add" ? "Admin staff added successfully" : "Admin staff updated successfully",
        severity: "success",
      })
      handleCloseDialog()
      dispatch(resetAdminStaffState())
    }
  }, [success, dialogMode, dispatch])

  useEffect(() => {
    if (error) {
      setAlert({
        open: true,
        message: error,
        severity: "error",
      })
      dispatch(resetAdminStaffState())
    }
  }, [error, dispatch])

  const handleOpenAddDialog = () => {
    setDialogMode("add")
    setFormData({
      name: "",
      email: "",
      password: "",
      status: "active",
      permissions: {},
    })
    setOpenDialog(true)
  }

  const handleOpenEditDialog = (staff) => {
    setDialogMode("edit")
    setCurrentStaffId(staff._id)
    setFormData({
      name: staff.name || "",
      email: staff.email || "",
      password: "", // Don't set password for edit
      status: staff.status || "active",
      permissions: staff.permissions || {},
    })
    setOpenDialog(true)
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setCurrentStaffId(null)
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value,
    })
  }

  const handleStatusChange = (e) => {
    setFormData({
      ...formData,
      status: e.target.value,
    })
  }

  const handlePermissionChange = (permissionId, value) => {
    setFormData({
      ...formData,
      permissions: {
        ...formData.permissions,
        [permissionId]: value === "Yes",
      },
    })
  }

  const handleSubmit = () => {
    if (dialogMode === "add") {
      dispatch(createAdminStaff(formData))
    } else {
      dispatch(updateAdminStaff({ id: currentStaffId, adminStaffData: formData }))
    }
  }

  const handleUpdateStatus = (id, newStatus) => {
    dispatch(updateAdminStaff({ id, adminStaffData: { status: newStatus } }))
  }

  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false })
  }

  return (
    <DashboardLayout>
      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleCloseAlert} severity={alert.severity} sx={{ width: "100%" }}>
          {alert.message}
        </Alert>
      </Snackbar>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h4" component="h1">
          Admin Staff: {adminStaff.length}
        </Typography>
        <Button variant="contained" onClick={handleOpenAddDialog} sx={{ bgcolor: "#008069", color: "white" }}>
          Add Admin Staff
        </Button>
      </Box>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>SR. NO.</TableCell>
                <TableCell>NAME</TableCell>
                <TableCell>EMAIL</TableCell>
                <TableCell>STATUS</TableCell>
                <TableCell>OPTION</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : adminStaff.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No admin staff found
                  </TableCell>
                </TableRow>
              ) : (
                adminStaff.map((staff, index) => (
                  <TableRow key={staff._id}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{staff.name}</TableCell>
                    <TableCell>{staff.email}</TableCell>
                    <TableCell>{staff.status === "active" ? "Active" : "Inactive"}</TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        onClick={() => handleOpenEditDialog(staff)}
                        sx={{ bgcolor: "#333", color: "white" }}
                      >
                        Update
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogMode === "add" ? "Add Admin Staff" : "Edit Admin Staff"}
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="h6" gutterBottom>
            Basic Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleInputChange}
                margin="normal"
                required={dialogMode === "add"}
                helperText={dialogMode === "edit" ? "Leave blank to keep current password" : ""}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <RadioGroup row name="status" value={formData.status} onChange={handleStatusChange}>
                  <FormControlLabel value="active" control={<Radio />} label="Active" />
                  <FormControlLabel value="inactive" control={<Radio />} label="Inactive" />
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>

          <Box sx={{ mt: 4, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Admin Staff Accesses
            </Typography>
            <Divider />
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width={80}>SR. NO.</TableCell>
                  <TableCell>NAME</TableCell>
                  <TableCell>ROLE</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {permissionsList.map((permission, index) => (
                  <TableRow key={permission.id}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{permission.name}</TableCell>
                    <TableCell>
                      <FormControl variant="outlined" size="small" fullWidth>
                        <Select
                          value={formData.permissions[permission.id] ? "Yes" : "No"}
                          onChange={(e) => handlePermissionChange(permission.id, e.target.value)}
                        >
                          <MenuItem value="No">No</MenuItem>
                          <MenuItem value="Yes">Yes</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={loading}
            sx={{ bgcolor: "#008069" }}
          >
            {loading ? <CircularProgress size={24} /> : dialogMode === "add" ? "Add" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  )
}

export default AdminStaffPage

